import { useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FireflyButton,
  Text,
  useToastMessage,
} from '@equip.health/ui';

import { useBulkProviderTeamAssignmentsMutation } from '~/store/services/techOps';

import { TechOpsToasts } from './toastMessages';
import RecentlySubmittedJobPanel from './RecentlySubmittedJobPanel';

type BulkJobSubmitProps = {
  topLevelId: string;
};

const BulkJobSubmit = ({ topLevelId }: BulkJobSubmitProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submittedJob, setSubmittedJob] =
    useState<OpsBulkProviderJobResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createToast } = useToastMessage();
  const [submitJob, { isLoading: isSubmitting }] =
    useBulkProviderTeamAssignmentsMutation();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) {
        setSelectedFile(file);
      }
    }
  };

  const handleSubmitJob = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const data = await submitJob(formData).unwrap();

      setSubmittedJob(data);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      createToast(TechOpsToasts.submitJob.error());
    }
  };

  return (
    <>
      {/* Bulk Job Submit Section */}
      <ChakraBox
        background="neutral.background.secondary"
        borderRadius="8px"
        marginBottom="32px"
        padding="24px"
      >
        <Text marginBottom="16px" variant="h5">
          Bulk Job Submission
        </Text>

        <ChakraFlex alignItems="center" gap="16px" marginBottom="16px">
          <ChakraBox flex="1">
            <input
              accept=".csv,.txt"
              id={`${topLevelId}-file-input`}
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                width: '100%',
              }}
              type="file"
            />
          </ChakraBox>
          {selectedFile && (
            <FireflyButton
              id={`${topLevelId}-clear-file`}
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              variant="tertiary"
            >
              Clear
            </FireflyButton>
          )}
          <FireflyButton
            id={`${topLevelId}-submit-job`}
            isDisabled={!selectedFile || isSubmitting}
            isLoading={isSubmitting}
            onClick={handleSubmitJob}
          >
            Start Job
          </FireflyButton>
        </ChakraFlex>

        {selectedFile && (
          <Text color="neutral.secondary" variant="caption">
            Selected file: {selectedFile.name}
          </Text>
        )}
      </ChakraBox>

      <RecentlySubmittedJobPanel submittedJob={submittedJob} />
    </>
  );
};

export default BulkJobSubmit;
